import React from 'react'
import { createRoot } from 'react-dom/client'
import { HostEnvironment } from '@typesApp/Environment'
import { AppConfig, Widget, WidgetConfig, WidgetInstance } from '@typesApp/Widget'
import { Logger } from '@utils/logger'
import { normalizeBrand } from '@utils/normalizeBrand'
import { BasicWidgetApp, WidgetApp } from './WidgetApp'

export const createWidgetHolder = (
  AppElementType: React.ElementType,
  appId: string,
  fallbackSelector = '#insuranceApp'
): Widget<AppConfig> => {
  const widgetHolder: Widget<AppConfig> = {
    new: (config: AppConfig): WidgetInstance => {
      const selector: string = config?.selector ?? fallbackSelector
      widgetHolder.selector = selector

      const node = getRootNode(selector)
      const root = widgetHolder.root ?? createRoot(node)
      if (!widgetHolder.root) {
        widgetHolder.root = root
      }

      initEnvProps(config?.environment)

      return {
        render: () => {
          root.render(
            <React.StrictMode>
              <WidgetApp appId={appId} brand={config?.environment?.brand} locale={config?.environment?.locale}>
                <AppElementType {...config} />
              </WidgetApp>
            </React.StrictMode>
          )
        },
      }
    },
    close: () => {
      unmount(widgetHolder)
    },
  }

  return widgetHolder
}

const getRootNode = (selector: string): HTMLElement => {
  const node = document.querySelector<HTMLElement>(selector)
  if (!node) {
    Logger.error(`Unable to create widget, selector is not valid -- ${selector}`)
    throw Error(`Selector is not valid -- ${selector}`)
  }
  return node
}

/**
 * Make some config items available outside of hooks
 */
const initEnvProps = (props: HostEnvironment) => {
  const host = props?.host
  const brand = props?.brand
  const locale = props?.locale
  const currency = props?.currency
  const localEnv = props?.localEnv

  if (!host || !brand || !locale || !currency) {
    return
  }

  window.directBilling = {
    ...window.directBilling,
    env: {
      ...window.directBilling?.env,
      host: host,
      brand: normalizeBrand(brand),
      locale: locale,
      currency: currency,
      localEnv: localEnv,
    },
  }
}

const unmount = <T extends WidgetConfig>(widget: Widget<T>) => {
  if (!widget?.selector || !widget?.root) return

  try {
    delete widget['selector']
    const root = widget?.root
    root?.unmount()
    delete widget['root']
  } catch (error) {
    Logger.warn('Error unmounting widget', error)
  }
}

export const createBasicWidgetHolder = (
  AppElementType: React.ElementType,
  fallbackSelector = '#insuranceInitializerApp'
): Widget<WidgetConfig> => {
  const widgetHolder: Widget<WidgetConfig> = {
    new: (config: WidgetConfig): WidgetInstance => {
      const selector: string = config?.selector ?? fallbackSelector
      widgetHolder.selector = selector

      const node = getRootNode(selector)
      const root = widgetHolder.root ?? createRoot(node)
      if (!widgetHolder.root) {
        widgetHolder.root = root
      }

      return {
        render: () => {
          root.render(
            <React.StrictMode>
              <BasicWidgetApp>
                <AppElementType {...config} />
              </BasicWidgetApp>
            </React.StrictMode>
          )
        },
      }
    },
    close: () => {
      unmount(widgetHolder)
    },
  }

  return widgetHolder
}
